import { apiClient } from './api';

export interface SystemStats {
  users: {
    total: number;
    byRole: Record<string, number>;
    recentLogins: number;
  };
  devices: {
    total: number;
    active: number;
    offline: number;
  };
  alerts: {
    critical: number;
    warning: number;
    info: number;
    total: number;
    unresolved: number;
  };
  system: {
    uptime: number;
    memoryUsage: {
      heapUsed: number;
      heapTotal: number;
    };
  };
  lastUpdated: string;
}

export interface AuditLog {
  id: string;
  user_id: string;
  user_email?: string;
  action: string;
  resource: string;
  resource_id?: string;
  ip_address?: string;
  user_agent?: string;
  success: boolean;
  error_message?: string;
  metadata?: Record<string, unknown>;
  timestamp: string;
}

export interface AuditLogsResponse {
  logs: AuditLog[];
  total: number;
  page: number;
  totalPages: number;
}

// === API Dashboard Types ===

export interface ServiceStatus {
  mqtt: { status: 'connected' | 'disconnected' };
  websocket: { status: 'active' | 'inactive'; clientCount: number };
  database: { status: 'connected' | 'error'; pool: { total: number; idle: number; waiting: number } };
  schedulers: {
    main: { status: 'running' | 'stopped'; jobCount: number };
    autoDosing: { status: 'running' | 'stopped' };
    notifications: { status: 'running' | 'stopped' };
  };
  gnn: { status: 'running' | 'stopped' };
}

export interface EndpointEntry {
  method: string;
  path: string;
  controller: string;
  category: string;
}

export interface EndpointsResponse {
  endpoints: EndpointEntry[];
  total: number;
}

export interface ModuleEntry {
  name: string;
  category: string;
  description: string;
}

export interface ModulesResponse {
  modules: Record<string, ModuleEntry[]>;
  totalCount: number;
}

export const adminService = {
  /**
   * Get system statistics for admin dashboard
   */
  async getSystemStats(): Promise<SystemStats> {
    return apiClient.get<SystemStats>('/admin/stats');
  },

  async getServiceStatus(): Promise<ServiceStatus> {
    return apiClient.get<ServiceStatus>('/admin/services');
  },

  async getEndpoints(): Promise<EndpointsResponse> {
    return apiClient.get<EndpointsResponse>('/admin/endpoints');
  },

  async getModules(): Promise<ModulesResponse> {
    return apiClient.get<ModulesResponse>('/admin/modules');
  },

  async controlService(service: string, action: 'start' | 'stop' | 'restart'): Promise<{ service: string; action: string; success: boolean }> {
    return apiClient.post(`/admin/services/${service}/control`, { action });
  },

  async getAuditLogs(params: {
    page?: number;
    limit?: number;
    action?: string;
    userId?: string;
    search?: string;
  }): Promise<AuditLogsResponse> {
    return apiClient.get<AuditLogsResponse>('/admin/audit-logs', { params });
  },
};

export default adminService;
